import React, { useContext, useEffect } from "react";
import { DefaultErrorMessage, FormKeys, ZomentumEntities } from "@/Contracts";
import {
	AddLogEmailRequest,
	ESalesActivityType,
	LogEmailFormValues,
	SalesActivity
} from "@/Contracts/Others/SalesActivity";
import SalesActivityActionCreators from "../../../../store/actions/SalesActivity";
import ZMessage from "@molecules/ZMessage";
import { IAddSalesActivityDrawerProps } from "../interface";
import Form from "antd/lib/form";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../../../store";
import ZLogEmailDrawer from "../../../../templates/ZDrawers/ZSalesActivities/ZLogEmail";
import moment from "moment";
import SalesActivityAddWrapper from "../Wrappers/AddWrapper";
import SalesActivityEditWrapper from "../Wrappers/EditWrapper";
import {
	ILogEmailUIProps,
	IOnlyLogEmailUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import { useFeatureFlag } from "@/Hooks/useFeature";
import useDatePattern from "@/Hooks/useDatePattern";
import { ParentContext } from "../../../../store/context/ParentContext";
import { isPartnerApp, isVendorApp } from "@/Utils";
import ZModalConfirm from "@atoms/ZModalConfirmation";

const LogEmail: React.FC<IAddSalesActivityDrawerProps> = ({
	visible = false,
	isEditMode = false,
	onClose,
	onSubmit
}) => {
	const parentContext = useContext(ParentContext);
	const { currentActiveSalesActivity } = useSalesActivityModuleSelector(
		(state: SalesActivityModuleState) => {
			return {
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		}
	);
	const { userData } = parentContext.user;
	const { isFormEdited } = parentContext.setting;

	const [form] = Form.useForm();
	const dispatch = useSalesActivityModuleDispatch();

	const datePattern = useDatePattern();

	useEffect(() => {
		if (visible) {
			if (
				isEditMode &&
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const feed = currentActiveSalesActivity.feed_details;
				form.setFieldsValue({
					title: feed.title,
					description: feed.description,
					date: moment(feed.date_time),
					time: moment(feed.date_time),
					relation:
						currentActiveSalesActivity?.feed_details.entity_type,
					author_id: feed.author_id
				});
			}
		}
	}, [visible, currentActiveSalesActivity]);

	const handleClose = () => {
		form.resetFields();
		onClose && onClose();
		parentContext.setting.resetFormEdited?.(FormKeys.AddEditEmailLog);
	};

	const closeLogEmailDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditEmailLog]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} email log?`,
				onOk: handleClose
			});
		}
	};

	const getFormValues = (addLogEmailFormValues: LogEmailFormValues) => {
		const addLogEmailRequest = new AddLogEmailRequest();
		addLogEmailRequest.title = addLogEmailFormValues.title;
		addLogEmailRequest.description = addLogEmailFormValues.description;
		const dateTime = new Date();
		dateTime.setDate(1);
		dateTime.setFullYear(addLogEmailFormValues.date.year());
		dateTime.setMonth(addLogEmailFormValues.date.month());
		dateTime.setDate(addLogEmailFormValues.date.date());
		dateTime.setHours(addLogEmailFormValues.time.hours());
		dateTime.setMinutes(addLogEmailFormValues.time.minutes());
		dateTime.setSeconds(0);
		dateTime.setMilliseconds(0);
		addLogEmailRequest.date_time = dateTime;
		addLogEmailRequest.author_id =
			addLogEmailFormValues.author_id ?? userData?.id;
		addLogEmailRequest.entity_type = addLogEmailFormValues.relation;
		if (
			addLogEmailFormValues.relation === ZomentumEntities.Opportunity &&
			isPartnerApp()
		) {
			addLogEmailRequest.entity_id = addLogEmailFormValues.opportunity;
		} else if (addLogEmailFormValues.relation === ZomentumEntities.Client) {
			addLogEmailRequest.entity_id = addLogEmailFormValues.client_company;
		} else if (
			addLogEmailFormValues.relation === ZomentumEntities.VendorClient
		) {
			addLogEmailRequest.entity_id =
				addLogEmailFormValues.vendor_client_company;
		} else if (
			addLogEmailFormValues.relation === ZomentumEntities.VendorCompany
		) {
			addLogEmailRequest.entity_id = addLogEmailFormValues.company;
		} else if (
			(addLogEmailFormValues.relation === ZomentumEntities.Opportunity ||
				addLogEmailFormValues.relation ===
					ZomentumEntities.VendorOpportunity) &&
			isVendorApp()
		) {
			addLogEmailRequest.entity_id =
				addLogEmailFormValues.vendor_opportunity;
		}
		return addLogEmailRequest;
	};

	const addLogEmail = async (addLogEmailFormValues: LogEmailFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			const addLogEmailRequest = getFormValues(addLogEmailFormValues);
			const addLogEmailResponse = await dispatch(
				SalesActivityActionCreators.addLogEmail(addLogEmailRequest)
			);
			if (onSubmit) {
				onSubmit(addLogEmailResponse.addLogEmail);
			}
			handleClose();
			ZMessage.success("Email log added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const editLogEmail = async (editLogEmailFormValues: LogEmailFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			if (
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const editLogEmailRequest: SalesActivity = {
					...currentActiveSalesActivity
				};
				const formValues = getFormValues(editLogEmailFormValues);
				editLogEmailRequest.feed_details = {
					...currentActiveSalesActivity.feed_details,
					...formValues
				};
				const editLogEmailResponse = await dispatch(
					SalesActivityActionCreators.editLogEmail(
						editLogEmailRequest
					)
				);
				if (onSubmit) {
					onSubmit(
						editLogEmailResponse.editLogEmail,
						form.getFieldValue(ZomentumEntities.Opportunity)
					);
				}
				handleClose();
				ZMessage.success("Email log edited successfully");
				// eslint-disable-next-line no-empty
				if (isPartnerApp()) {
				}
			} else {
				ZMessage.error(DefaultErrorMessage);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditEmailLog] &&
			parentContext.setting.setFormEdited?.(FormKeys.AddEditEmailLog);
	};

	const isNewDocumentSendView = useFeatureFlag("new_document_send_view");
	const addLogEmailUIProps: IOnlyLogEmailUIProps = {
		onSubmit: isEditMode ? editLogEmail : addLogEmail,
		currentActiveSalesActivityId: currentActiveSalesActivity?.id,
		subject: currentActiveSalesActivity?.title,
		isEmailLogBodyAvailable:
			currentActiveSalesActivity?.feed_details
				?.is_email_log_body_html_present,
		dateFormat: datePattern,
		systemEmail: isNewDocumentSendView
			? currentActiveSalesActivity?.feed_details?.system_email
			: undefined,
		handleFormChange
	};

	return isEditMode ? (
		<SalesActivityEditWrapper<ILogEmailUIProps, IOnlyLogEmailUIProps>
			visible={visible}
			onClose={closeLogEmailDrawer}
			Component={ZLogEmailDrawer}
			form={form}
			formUIProps={addLogEmailUIProps}
			salesActivityType={ESalesActivityType.LOGEMAIL}
		/>
	) : (
		<SalesActivityAddWrapper<ILogEmailUIProps, IOnlyLogEmailUIProps>
			visible={visible}
			onClose={closeLogEmailDrawer}
			Component={ZLogEmailDrawer}
			form={form}
			formUIProps={addLogEmailUIProps}
			salesActivityType={ESalesActivityType.LOGEMAIL}
		/>
	);
};

export default LogEmail;
